// Gestures → intents.
//
// Two rules hold this file together.
//
// 1. Every builder reads the CANONICAL record, never the rendered one. `LaneClip`
//    keeps four of ClipData's thirteen fields, and an `upsertClip` carries a
//    whole ClipData — so building an edit from the view model would silently
//    blank warp markers, loop points and file path on every drag.
//
// 2. Geometry is only touched when the gesture is about geometry. A Live
//    receiver rebuilds a clip from its audio file whenever startTime or duration
//    differ (src/realtime/apply-ops.ts:263); a rename that perturbed startTime by
//    a float hair would cost someone a re-import and lose warp detail.

import { needsBrowserRender, warpRenderHash } from "@shared/sync/clip-render.js";
import { MAX_DETUNE, MAX_TRANSPOSE, MAX_WARP_MARKERS, type ClipData, type ClipRender, type WarpMarkerData, warpSettingsOf, warpSettingsKey, WARP_SETTINGS_DEFAULTS, type WarpSettingsData } from "@shared/sync/protocol.js";
import { CENTER_PAN, UNITY_VOLUME, clampPan, clampVolume } from "@shared/mixer.js";
import type { InstrumentId } from "@shared/instruments.js";
import type { Intent } from "@shared/sync/intents.js";
import type { LibraryPlacement } from "@shared/api-types.js";
import {
  MAX_SCENES,
  MAX_SCENE_NAME_LENGTH,
  MAX_CUE_POINTS,
  MAX_CUE_NAME_LENGTH,
  MAX_RETURNS,
  MAX_DEVICES,
  MAX_DEVICE_NAME_LENGTH,
  clampParam,
  cloneDevices,
  type AcceptedOp,
  type ChainOwner,
  type SceneName,
  type CanonicalClipState,
  type CanonicalRoom,
  type CanonicalTrackState,
  type TrackField,
  type TrackFieldValue,
  type TrackKind,
} from "@shared/sync/state.js";
import { ASSERT_EPS } from "@shared/sync/canonical.js";
import { clipDataOf as sharedClipDataOf } from "@shared/sync/mutations.js";
import { parseTrackName, renameKeepingToken } from "@shared/sync/track-id.js";
import {
  canonicalNotes,
  MAX_NOTES_PER_CLIP,
  notesDigest,
  type WireNote,
} from "@shared/midi/notes.js";
import { scaleLoopNotes } from "@web-shared/lib/piano-roll";

/** Strip a canonical record back to the wire's clip payload. One shared
 * implementation (mutations.ts) so a MIDI clip's notes/kind/digest survive a
 * viz paste or rename the same way they survive a move on the DO. */
export function clipDataOf(clip: CanonicalClipState): ClipData {
  return sharedClipDataOf(clip);
}

export function findClip(room: CanonicalRoom, clipId: string): CanonicalClipState | null {
  return room.clips.find((c) => c.id === clipId) ?? null;
}

/**
 * Whether there is anything to make a copy of this clip FROM. One predicate,
 * because a paste builder that drops a clip and a button that stays enabled for
 * it are the same bug seen from two sides.
 *
 * An audio clip is its object, so a key-less one (a clip the room never had
 * bytes for) cannot be recreated. A MIDI clip is its notes — and the room ships
 * an over-cap clip with none of them (`notesOmitted`, shared/midi/notes.ts), so
 * copying that would create an EMPTY clip where the notes were, which is worse
 * than refusing. A slot clip has no timeline position to copy to.
 */
export function copyable(clip: CanonicalClipState): boolean {
  if (clip.slot !== undefined) return false;
  if ((clip.clipKind ?? "audio") === "midi") return Array.isArray(clip.notes);
  return clip.audioKey !== null;
}

function upsert(
  clip: CanonicalClipState,
  patch: Partial<ClipData>,
  trackId = clip.trackId,
): Intent {
  return {
    kind: "upsertClip",
    id: clip.id,
    tempId: `edit_${clip.id}`,
    trackId,
    baseRev: clip.rev,
    clip: { ...clipDataOf(clip), ...patch },
    // Updates keep whatever the room already holds; only creates must name a key.
    audioKey: null,
  };
}

/** The clip's `render`, or a request for one. Warp Live can't take (markers,
 * pitch, the mode knobs) is carried to it by a render of the clip; an edit
 * that makes such warp says so by leaving a render with no key, which the
 * editing tab then fills (`completeRender`). A render already there stays: it
 * goes stale by its hash, not by being dropped. */
function wantRender(clip: CanonicalClipState): { render: ClipRender } {
  return { render: clip.render ?? { hash: "pending", tempo: 0 } };
}

/**
 * Ask for a render of a clip that never has: one made before `render`
 * existed, or in a flow that does not make one (a web upload). The editing
 * tab then renders it like any other pending clip. Null for MIDI, a clip that
 * already has a render (stale or not — that is for the render pass), or one
 * whose warp Live can take as it is.
 */
export function requestRender(room: CanonicalRoom, clipId: string): Intent | null {
  const clip = findClip(room, clipId);
  if (!clip || clip.render || !clip.audioKey || !needsBrowserRender(clip, room.tempo)) return null;
  return upsert(clip, wantRender(clip));
}

/**
 * Record the finished render of a clip: its object `key`, rendered at `tempo`
 * from the clip as it stood when the render began (`hash` = `warpRenderHash`
 * of it then). Null when the clip is gone, is not wanting a render, or has
 * been edited since: the render is of a clip that no longer exists, and the
 * caller renders again.
 */
export function completeRender(room: CanonicalRoom, clipId: string, render: { key: string; hash: string; tempo: number }): Intent | null {
  const clip = findClip(room, clipId);
  if (!clip || clip.clipKind === "midi" || !clip.render || !(render.tempo > 0)) return null;
  if (warpRenderHash(clip) !== render.hash) return null;
  return upsert(clip, { render: { key: render.key, hash: render.hash, tempo: render.tempo } });
}

/**
 * Move clips by a beat delta, optionally onto another lane.
 *
 * Ordered so a clip vacates a span before another occupies it: intents inside
 * one envelope see each other's effects, so a rightward run must move its
 * rightmost member first or it collides with its own neighbour.
 */
export function moveClips(
  room: CanonicalRoom,
  clipIds: string[],
  deltaBeats: number,
  trackIdFor?: (clip: CanonicalClipState) => string,
): Intent[] {
  const clips = clipIds
    .map((id) => findClip(room, id))
    .filter((c): c is CanonicalClipState => c !== null);

  const ordered = [...clips].sort((a, b) =>
    deltaBeats >= 0 ? b.startTime - a.startTime : a.startTime - b.startTime,
  );

  return ordered.map((clip) =>
    upsert(
      clip,
      { startTime: Math.max(0, clip.startTime + deltaBeats) },
      trackIdFor?.(clip) ?? clip.trackId,
    ),
  );
}

/**
 * Copies of the clips, `deltaBeats` along (and on another lane, when the
 * caller says so) — an alt-drag. The originals stay; the copies are one
 * gesture, so a copy the room refuses (an overlap) takes the others down
 * with it rather than landing half a selection. Audio copies reuse their
 * object (a key-less clip has nothing to copy), MIDI copies carry their
 * notes; slot clips have no timeline position and are skipped.
 */
export function copyClips(
  room: CanonicalRoom,
  clipIds: string[],
  deltaBeats: number,
  trackIdFor?: (clip: CanonicalClipState) => string,
): Intent[] {
  const clips = clipIds
    .map((id) => findClip(room, id))
    .filter((c): c is CanonicalClipState => c !== null)
    .filter(copyable);
  const gestureId = clips.length > 1 ? mintTempId("gesture") : undefined;
  return clips.map((clip, i) => {
    const trackId = trackIdFor?.(clip) ?? clip.trackId;
    const track = room.tracks.find((t) => t.id === trackId);
    const target = track && (track.trackKind ?? "audio") === (clip.clipKind ?? "audio") ? trackId : clip.trackId;
    return {
      kind: "upsertClip",
      id: null,
      tempId: mintTempId(`copy_${i}`),
      trackId: target,
      baseRev: null,
      clip: { ...clipDataOf(clip), startTime: Math.max(0, clip.startTime + deltaBeats) },
      audioKey: clip.audioKey,
      ...(gestureId ? { gestureId } : {}),
    };
  });
}

/**
 * Change a clip's length, from either edge.
 *
 * A warped clip's loop settings are rebuilt from its markers on the receiving
 * side (`loopFrom`, src/clipsync.ts:116), so the end marker has to travel with
 * the length or the recreated clip loops over the wrong span. An unwarped clip
 * ignores markers entirely — and clamps to its source file's length, so growing
 * one past its audio will come back corrected.
 */
export function resizeClip(
  room: CanonicalRoom,
  clipId: string,
  edge: "start" | "end",
  deltaBeats: number,
  minDuration: number,
): Intent | null {
  const clip = findClip(room, clipId);
  if (!clip) return null;

  if (edge === "end") {
    const duration = Math.max(minDuration, clip.duration + deltaBeats);
    const applied = duration - clip.duration;
    return upsert(clip, {
      duration,
      ...(clip.isWarped
        ? { endMarker: clip.endMarker + applied, loopEnd: clip.loopEnd + applied }
        : {}),
    });
  }

  // Dragging the left edge moves the start and shortens by the same amount, so
  // the clip's right edge stays put.
  const maxShift = clip.duration - minDuration;
  const shift = Math.min(Math.max(deltaBeats, -clip.startTime), maxShift);
  return upsert(clip, {
    startTime: clip.startTime + shift,
    duration: clip.duration - shift,
    ...(clip.isWarped
      ? { startMarker: clip.startMarker + shift, loopStart: clip.loopStart + shift }
      : {}),
  });
}

/** Scalar-only edits. Geometry is copied through untouched, so Live takes the
 * cheap in-place branch rather than deleting and re-importing the clip. */
export function setClipFields(
  room: CanonicalRoom,
  clipId: string,
  patch: Pick<Partial<ClipData>, "name" | "muted" | "color" | "sourceTempo">,
): Intent | null {
  const clip = findClip(room, clipId);
  return clip ? upsert(clip, patch) : null;
}

/**
 * The inspector's geometry write: an exact start and length, typed rather
 * than dragged. A typed START is a move — the same audio in a new place, so
 * the markers stay exactly as a drag leaves them; a typed LENGTH is an
 * end-edge resize under `resizeClip`'s discipline (a warped clip's end
 * marker and loop end follow the edge, an unwarped clip's stay put). So a
 * typed value and the equivalent drag land identically.
 */
export function setClipGeometry(
  room: CanonicalRoom,
  clipId: string,
  startTime: number,
  duration: number,
  minDuration: number,
): Intent | null {
  const clip = findClip(room, clipId);
  if (!clip || clip.slot !== undefined) return null;
  const start = Math.max(0, startTime);
  const length = Math.max(minDuration, duration);
  const grow = length - clip.duration;
  return upsert(clip, {
    startTime: start,
    duration: length,
    ...(clip.isWarped && grow !== 0 ? { endMarker: clip.endMarker + grow, loopEnd: clip.loopEnd + grow } : {}),
  });
}

/** Live's warp modes, by the value the wire carries (the SDK's enum). */
export const WARP_MODES: ReadonlyArray<{ value: number; label: string }> = [
  { value: 0, label: "Beats" },
  { value: 1, label: "Tones" },
  { value: 2, label: "Texture" },
  { value: 3, label: "Re-Pitch" },
  { value: 4, label: "Complex" },
  { value: 6, label: "Complex Pro" },
];

/**
 * Warp on/off and the warp mode of an audio clip — the Set's, applied in
 * place by a receiver (the SDK has setters for both). Warping off also
 * turns looping off (Live does); warping on starts from the whole object
 * as its markers (0..duration, no loop), the frame every one-shot on the
 * wire already sits in, so nothing about what plays changes until a brace
 * is set. Null for MIDI, or when nothing would change.
 */
export function setClipWarp(
  room: CanonicalRoom,
  clipId: string,
  warp: { isWarped: boolean; warpModeValue?: number },
): Intent | null {
  const clip = findClip(room, clipId);
  if (!clip || clip.clipKind === "midi") return null;
  const mode = warp.warpModeValue ?? clip.warpModeValue;
  if (!WARP_MODES.some((m) => m.value === mode)) return null;
  if (clip.isWarped === warp.isWarped && clip.warpModeValue === mode) return null;
  if (!warp.isWarped) {
    // Live, unwarped, plays the file at its own speed: its length in beats is
    // now the seconds it really is at this tempo (measured, 2026-10-30).
    const known = clip.sourceTempo !== undefined && clip.sourceTempo > 0 && room.tempo > 0;
    const duration = known ? (clip.duration * room.tempo) / clip.sourceTempo! : clip.duration;
    return upsert(clip, { isWarped: false, looping: false, warpModeValue: mode, duration });
  }
  if (clip.isWarped) return upsert(clip, { warpModeValue: mode });
  // The markers and brace stay where they were: warp off and on again must
  // not throw a start marker or a loop away (playback-audit §3). Looping
  // itself stays off until a brace is set — warp off turned it off, as Live
  // does, and warp on in Live does not turn it back on.
  return upsert(clip, {
    isWarped: true,
    warpModeValue: mode,
    ...markersForWarpOn(clip),
    // An unwarped clip played at its file's speed, so on the room's grid
    // its beats WERE the room's tempo: warping at that tempo changes
    // nothing audible until the tempo moves or a marker does (Live's
    // "warp on" guesses a tempo the same way). A known one is kept.
    ...(clip.sourceTempo === undefined && room.tempo > 0 ? { sourceTempo: room.tempo } : {}),
  });
}

/**
 * What "warp on" keeps of an unwarped clip's markers. Unwarped, the clip
 * played the top of its object for its own length (`objectStart`), whatever
 * its markers said; warped, the markers are read as file positions. A span
 * that overlaps that frame is kept (§3 above). One that lies entirely past
 * it never described anything the clip played, and over a region render
 * (a file of only the clip's span) it is positions past the end of the
 * file: a clip split off the back of a 64-beat one shipped 56..64 over
 * 8 beats of audio by senders before 2026-10-27, and the browser's warp-on
 * then pitched silence (jam j_mvrf54fk_758c0a). Such a span falls back to
 * the one-shot frame, 0..duration, so what plays does not change.
 */
function markersForWarpOn(clip: CanonicalClipState): Partial<ClipData> {
  if (clip.startMarker < clip.duration - ASSERT_EPS) return {};
  return { startMarker: 0, endMarker: clip.duration, loopStart: 0, loopEnd: clip.duration };
}

/**
 * Replace a warped audio clip's warp markers (the browser clip editor):
 * sorted by beat, a beat held twice keeping its last, and refused unless
 * the file seconds never run backwards (the room's rule — a map that
 * folds back on itself plays nothing sensible). An empty list clears
 * them, so the clip falls back to `sourceTempo`. Geometry rides through
 * untouched and markers are no Live content field, so a Set never
 * rebuilds for it. Null for MIDI, unwarped clips, an invalid or over-cap
 * list, or no change.
 */
export function setClipWarpMarkers(
  room: CanonicalRoom,
  clipId: string,
  markers: readonly WarpMarkerData[],
): Intent | null {
  const clip = findClip(room, clipId);
  if (!clip || !clip.isWarped || clip.clipKind === "midi") return null;
  const byBeat = new Map<number, WarpMarkerData>();
  for (const m of markers) {
    if (!Number.isFinite(m.beat) || !Number.isFinite(m.sec) || m.sec < 0) return null;
    byBeat.set(m.beat, { beat: m.beat, sec: m.sec });
  }
  const sorted = [...byBeat.values()].sort((a, b) => a.beat - b.beat);
  if (sorted.length > MAX_WARP_MARKERS) return null;
  for (let i = 1; i < sorted.length; i++) if (sorted[i]!.sec < sorted[i - 1]!.sec) return null;
  const before = clip.warpMarkers ?? [];
  if (
    before.length === sorted.length &&
    before.every((m, i) => Math.abs(m.beat - sorted[i]!.beat) < ASSERT_EPS && Math.abs(m.sec - sorted[i]!.sec) < 1e-6)
  ) {
    return null;
  }
  return upsert(clip, { warpMarkers: sorted, ...wantRender(clip) });
}

/**
 * The clip editor's "Bounce in place". The clip as it sounds, already
 * rendered to `audioKey`, replaces the clip's own audio. The clip keeps its
 * id and place, but everything that shaped the sound is baked in and
 * dropped: no warp markers, no transpose or detune, no loop brace, warped
 * straight at the room's tempo (the render IS at that tempo), so it keeps
 * following the room. New audio means a Set rebuilds the clip from the
 * render, so a browser-only warp reaches Live this way.
 *
 * The ORIGINAL is kept as a backup: a copy of the clip as it was before the
 * bounce (its own audio, warp markers, pitch and loop) goes into the
 * Session View on the same lane, in the first scene row no lane has a clip
 * in, so the edit can be reopened or re-bounced later. A clip that is
 * already a Session View clip is bounced in its slot and gets no backup.
 *
 * One gesture, so one undo takes both back. Empty for MIDI, a missing clip,
 * no tempo, or (for a timeline clip) a Session View with no empty row left.
 */
export function bounceInPlace(room: CanonicalRoom, clipId: string, audioKey: string, tempo: number): Intent[] {
  const clip = findClip(room, clipId);
  if (!clip || clip.clipKind === "midi" || !(tempo > 0) || !(clip.duration > 0)) return [];
  const gestureId = mintTempId("gesture");
  const name = bounceName(clip.name);
  const baked: ClipData = {
    ...clipDataOf(clip),
    isWarped: true,
    warpModeValue: 0,
    sourceTempo: tempo,
    looping: clip.slot !== undefined,
    startMarker: 0,
    endMarker: clip.duration,
    loopStart: 0,
    loopEnd: clip.duration,
    transpose: 0,
    detune: 0,
    name,
  };
  // New audio, a new object: its map is not the old one's, nor its knobs.
  delete baked.warpMarkers;
  delete baked.warpSettings;
  delete baked.render;
  const replace: Intent = {
    kind: "upsertClip",
    id: clip.id,
    tempId: `edit_${clip.id}`,
    trackId: clip.trackId,
    baseRev: clip.rev,
    clip: baked,
    audioKey,
    gestureId,
  };
  if (clip.slot !== undefined || !clip.audioKey) return [replace];
  const row = firstEmptySceneRow(room);
  if (row === null) return [];
  const backup: Intent = {
    kind: "upsertClip",
    id: null,
    tempId: mintTempId("slot"),
    trackId: clip.trackId,
    baseRev: null,
    clip: { ...clipDataOf(clip), startTime: 0, slot: row },
    audioKey: clip.audioKey,
    gestureId,
  };
  return [replace, backup];
}

/** The first Session View row (scene) with no clip on any lane, or null
 * when every row the room allows holds something. */
export function firstEmptySceneRow(room: CanonicalRoom): number | null {
  const used = new Set(room.clips.filter((c) => c.slot !== undefined).map((c) => c.slot));
  for (let row = 0; row < MAX_SCENES; row++) if (!used.has(row)) return row;
  return null;
}

/** A bounce's name: "Keys" → "Keys (bounce)", and a clip that already is
 * a bounce (Live's "(Bounce)" included) counts up, "(bounce 2)", "(bounce 3)",
 * so the new clip never reads as the one it came from. */
export function bounceName(name: string): string {
  const m = /^(.*?)\s*\(bounce(?:\s+(\d+))?\)\s*$/i.exec(name);
  if (!m) return `${name} (bounce)`;
  const n = m[2] ? Number(m[2]) + 1 : 2;
  return `${m[1]} (bounce ${n})`;
}

/**
 * Live's clip Transpose / Detune knobs — non-destructive: the object is
 * untouched and the browser shifts at playback. Rounded and clamped to the
 * knobs' ranges; a field left out keeps its value. Null for MIDI or no change.
 */
export function setClipPitch(
  room: CanonicalRoom,
  clipId: string,
  pitch: { transpose?: number; detune?: number },
): Intent | null {
  const clip = findClip(room, clipId);
  if (!clip || clip.clipKind === "midi") return null;
  const transpose =
    pitch.transpose === undefined ? (clip.transpose ?? 0) : clampInt(pitch.transpose, MAX_TRANSPOSE);
  const detune = pitch.detune === undefined ? (clip.detune ?? 0) : clampInt(pitch.detune, MAX_DETUNE);
  if (transpose === (clip.transpose ?? 0) && detune === (clip.detune ?? 0)) return null;
  return upsert(clip, { transpose, detune, ...wantRender(clip) });
}

/**
 * The warp-mode knobs (Beats' Preserve / Loop Mode / Envelope, Complex
 * Pro's Formants / Envelope) — browser-only, like pitch. A field left out
 * keeps its value; one set to Live's default is dropped, so the clip
 * carries only what differs from Live. Null for MIDI, an invalid value, or
 * no change.
 */
export function setClipWarpSettings(room: CanonicalRoom, clipId: string, patch: WarpSettingsData): Intent | null {
  const clip = findClip(room, clipId);
  if (!clip || clip.clipKind === "midi") return null;
  const merged = warpSettingsOf({ ...(clip.warpSettings ?? {}), ...patch });
  if (!merged) return null;
  const next: WarpSettingsData = {};
  for (const k of ["preserve", "loopMode", "envelope", "formants", "proEnvelope"] as const) {
    const v = merged[k];
    if (v !== undefined && v !== WARP_SETTINGS_DEFAULTS[k]) (next as Record<string, number>)[k] = v;
  }
  if (warpSettingsKey(next) === warpSettingsKey(clip.warpSettings)) return null;
  return upsert(clip, { warpSettings: next, ...wantRender(clip) });
}

function clampInt(v: number, max: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(-max, Math.min(max, Math.round(v)));
}

/** Loop on/off — meaningful on warped audio only; Live ignores it elsewhere
 * and a MIDI clip travels flattened. Geometry untouched. */
export function setClipLooping(room: CanonicalRoom, clipId: string, looping: boolean): Intent | null {
  const clip = findClip(room, clipId);
  if (!clip || !clip.isWarped || clip.clipKind === "midi") return null;
  return upsert(clip, { looping });
}

/**
 * The loop brace of a warped audio clip, in clip-internal beats — what Live
 * repeats once the clip plays past the loop's end. Kept inside the markers
 * (the object is a region render: nothing exists outside them) and at least
 * `minLength` long; switches looping on, since a brace nobody plays is
 * nothing. Null for MIDI (flattened on the wire), unwarped clips, or a
 * brace that doesn't fit.
 */
export function setClipLoop(
  room: CanonicalRoom,
  clipId: string,
  loopStart: number,
  loopEnd: number,
  minLength: number,
): Intent | null {
  const clip = findClip(room, clipId);
  if (!clip || !clip.isWarped || clip.clipKind === "midi") return null;
  const start = Math.max(clip.startMarker, loopStart);
  const end = Math.min(clip.endMarker, loopEnd);
  if (!(end - start >= minLength - ASSERT_EPS)) return null;
  if (clip.looping && Math.abs(start - clip.loopStart) < ASSERT_EPS && Math.abs(end - clip.loopEnd) < ASSERT_EPS) return null;
  return upsert(clip, { looping: true, loopStart: start, loopEnd: end });
}

/**
 * Replace a MIDI clip's notes. Geometry rides through untouched — the Live
 * receiver's cheap branch for a same-geometry, changed-digest upsert is an
 * in-place `notes` rewrite (apply-ops), so a piano-roll edit never costs a
 * clip rebuild. Canonicalised here so the digest we claim is the digest the
 * room will compute over what we sent. An over-cap list is not proposable at
 * all (the room would reject `missing-notes`), so it returns null rather
 * than proposing its own rejection.
 */
export function setClipNotes(
  room: CanonicalRoom,
  clipId: string,
  notes: readonly WireNote[],
): Intent | null {
  const clip = findClip(room, clipId);
  if (!clip || clip.clipKind !== "midi") return null;
  // Only what lies inside the clip: a flattened wire clip plays [0, duration)
  // once, and Live reads a clip back cut at its end (flattenMidiClip). A note
  // at or past the end, or one running over it, digested differently from the
  // clip every Live builds and the lane never converged (2026-10-30, the jam
  // fuzzer: a 3-beat pattern written into a 1-beat split tail).
  const inside = notes.flatMap((n) => {
    if (!(n.startTime < clip.duration - ASSERT_EPS)) return [];
    const duration = Math.min(n.duration, clip.duration - n.startTime);
    return duration > ASSERT_EPS ? [{ ...n, duration }] : [];
  });
  const canonical = canonicalNotes(inside);
  if (canonical.length > MAX_NOTES_PER_CLIP) return null;
  return upsert(clip, { notes: canonical, notesDigest: notesDigest(canonical) });
}

/**
 * Live's ×2 / ÷2 on a MIDI clip: the clip's length doubles (its notes
 * repeating in the new half) or halves (notes past the new end dropped),
 * markers following the length. One upsert — the room refuses a doubling
 * that would overlap the next clip on the lane. Null for audio, a missing
 * clip, an over-cap result, or a halving below the grid.
 */
export function scaleClipLoop(room: CanonicalRoom, clipId: string, factor: 2 | 0.5, minDuration: number): Intent | null {
  const clip = findClip(room, clipId);
  if (!clip || clip.clipKind !== "midi") return null;
  const duration = clip.duration * factor;
  if (duration < minDuration) return null;
  const canonical = canonicalNotes(scaleLoopNotes(clip.notes ?? [], clip.duration, factor));
  if (canonical.length > MAX_NOTES_PER_CLIP) return null;
  return upsert(clip, {
    duration,
    endMarker: duration,
    loopEnd: duration,
    notes: canonical,
    notesDigest: notesDigest(canonical),
  });
}

export function deleteClips(room: CanonicalRoom, clipIds: string[]): Intent[] {
  return clipIds
    .map((id) => findClip(room, id))
    .filter((c): c is CanonicalClipState => c !== null)
    .map((clip) => ({ kind: "deleteClip", id: clip.id, baseRev: clip.rev }));
}

/**
 * Place copies of `sources` onto ONE lane, the earliest of them at `atBeat` and
 * the rest keeping their relative spacing. Every member hunts its own gap, so a
 * blocked clip slides on its own while the others stay put —
 * `pasteClipBlock` below is the rigid alternative.
 *
 * An audio create reuses its source's own key, so paste costs no upload and
 * passes the room's `audioExists` gate by construction; a MIDI create carries
 * its notes instead. `copyable` is the test for having either, and slot clips
 * fail it — a Session View clip's payload still carries its `slot`
 * (shared/sync/mutations.ts `clipDataOf`), so letting one through would propose
 * a second clip in an occupied slot rather than anything on the timeline.
 *
 * The lane's KIND is the caller's to check: the room refuses a MIDI clip on an
 * audio lane and the reverse (merge-engine `upsertClip`), and this builder is
 * handed a lane id, not a room.
 *
 * `place` is the caller's collision resolver; returning null means "no room for
 * this one", and it is skipped rather than proposed into a rejection.
 */
export function pasteClips(
  sources: CanonicalClipState[],
  trackId: string,
  atBeat: number,
  place: (start: number, duration: number) => number | null,
): Intent[] {
  const usable = sources.filter(copyable);
  if (usable.length === 0) return [];

  const anchor = Math.min(...usable.map((c) => c.startTime));
  const gestureId = usable.length > 1 ? mintTempId("gesture") : undefined;
  const intents: Intent[] = [];

  for (const source of [...usable].sort((a, b) => a.startTime - b.startTime)) {
    const wanted = atBeat + (source.startTime - anchor);
    const start = place(wanted, source.duration);
    if (start === null) continue;
    intents.push({
      kind: "upsertClip",
      id: null,
      // Minted, not derived from the source id: two pastes of the same clip
      // would otherwise share a tempId and collide in the pending map.
      tempId: mintTempId("paste"),
      trackId,
      baseRev: null,
      clip: { ...clipDataOf(source), startTime: start },
      audioKey: source.audioKey,
      ...(gestureId ? { gestureId } : {}),
    });
  }
  return intents;
}

/** What a block paste could and could not do — the caller's material for telling
 *  someone why they got three clips instead of four. */
export interface BlockPaste {
  intents: Intent[];
  /** Sources with no lane to land on: the offset ran off the end of the Set, or
   *  the lane it pointed at is the wrong kind. */
  skipped: number;
  /** Nothing landed: some member could not fit anywhere inside the horizon. */
  blocked: boolean;
}

/**
 * Paste `sources` as ONE RIGID BLOCK, keeping both their relative beats and
 * their relative lanes.
 *
 * `pasteClips` collapses a selection onto a single lane and lets each member
 * hunt its own gap. That is right for a paste aimed at one lane, and wrong for
 * a selection that spans several: a kick, a bass and a hat over the same four
 * bars would come back as a horizontal run on one lane, silently. It also
 * SHEARS a same-lane selection in time, because a member that hits an
 * obstruction moves and its neighbours do not.
 *
 * So the whole block shifts together: find the smallest common `shift` at which
 * every member fits, by asking each lane where its own member could go and
 * taking the worst answer, until an answer stops moving. Monotonic, so it
 * converges; `blocked` when some member cannot fit anywhere ahead of it.
 *
 * `laneFor` names the lane each source should land on — the caller's mapping,
 * which is where the kind check belongs. `place` is its collision resolver,
 * per lane, exactly as `pasteClips` takes one; keeping it a callback is what
 * lets this file stay about intents and placement.ts stay about legality.
 */
export function pasteClipBlock(
  sources: CanonicalClipState[],
  laneFor: (clip: CanonicalClipState) => string | null,
  atBeat: number,
  place: (laneId: string, start: number, duration: number) => number | null,
): BlockPaste {
  const usable = sources.filter(copyable);
  if (usable.length === 0) return { intents: [], skipped: 0, blocked: false };

  const anchor = Math.min(...usable.map((c) => c.startTime));
  const placed: Array<{ clip: CanonicalClipState; laneId: string; wanted: number }> = [];
  let skipped = 0;
  for (const clip of usable) {
    const laneId = laneFor(clip);
    if (laneId === null) {
      skipped += 1;
      continue;
    }
    placed.push({ clip, laneId, wanted: Math.max(0, atBeat + (clip.startTime - anchor)) });
  }
  if (placed.length === 0) return { intents: [], skipped, blocked: false };

  let shift = 0;
  // One pass per member is the most a monotonic hunt can need; the bound is a
  // backstop against a float that never quite settles, not a real limit.
  for (let pass = 0; pass <= placed.length; pass++) {
    let worst = shift;
    for (const p of placed) {
      const at = place(p.laneId, p.wanted + shift, p.clip.duration);
      if (at === null) return { intents: [], skipped, blocked: true };
      worst = Math.max(worst, at - p.wanted);
    }
    if (worst <= shift + ASSERT_EPS) break;
    shift = worst;
  }

  const gestureId = placed.length > 1 ? mintTempId("gesture") : undefined;
  const intents: Intent[] = placed
    .sort((a, b) => a.wanted - b.wanted)
    .map((p) => ({
      kind: "upsertClip" as const,
      id: null,
      tempId: mintTempId("paste"),
      trackId: p.laneId,
      baseRev: null,
      clip: { ...clipDataOf(p.clip), startTime: p.wanted + shift },
      audioKey: p.clip.audioKey,
      ...(gestureId ? { gestureId } : {}),
    }));
  return { intents, skipped, blocked: false };
}

/** Local handle for a create — the room echoes it back on the accepted op. */
export function mintTempId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

export function createTrack(
  name: string,
  ord: number,
  trackKind: TrackKind = "audio",
  instrument?: InstrumentId,
): Extract<Intent, { kind: "createTrack" }> {
  return {
    kind: "createTrack",
    tempId: mintTempId("track"),
    name,
    ord,
    ...(trackKind === "midi" ? { trackKind: "midi" as const } : {}),
    ...(trackKind === "midi" && instrument ? { instrument } : {}),
  };
}

/** A MIDI lane's instrument. Null for an unknown or audio lane, or no change. */
export function setTrackInstrument(room: CanonicalRoom, trackId: string, instrument: InstrumentId): Intent | null {
  const track = room.tracks.find((t) => t.id === trackId);
  if (!track || track.trackKind !== "midi") return null;
  if ((track.instrument ?? "sine") === instrument) return null;
  return { kind: "setTrackInstrument", id: track.id, baseRev: track.rev, instrument };
}

/** A lane the server has named, and the rev it named it at. */
export interface CreatedLane {
  id: string;
  rev: number;
}

/**
 * A whole room, as the intents that would build it from nothing.
 *
 * This is how the browser's solo editor gets its work to the server. A local
 * room is edited in memory against ids it minted itself, so its edits cannot
 * be replayed into a jam that minted its own — the ids name nothing there.
 * Uploading the ROOM sidesteps that: every lane and clip is created fresh
 * under a tempId, which the merge engine resolves inside the envelope.
 *
 * It lands because the room is EMPTY when it arrives. A jam's Durable Object
 * hydrates from its base seed on the first `/state` or `/ws` — deliberately
 * not on `/intents` — so intents posted before either find nothing there and
 * become the room's content, the same "adopt what a client seeded" path the
 * extension's first joiner has always taken.
 *
 * TWO envelopes, because a lane's fields cannot name a lane that does not
 * exist yet: the merge engine resolves a tempId for a clip's `trackId` and
 * for nothing else, so mute, solo, arm, the fader and the pan have to wait
 * until the server has named the lane and said so (`trackCreated.tempId`).
 *
 * And NO shared gestureId. A gesture is all-or-nothing — one clip whose audio
 * the server cannot find would take the entire room down with it, which is
 * the worst possible failure for a save.
 */
export interface RoomUpload {
  /** Everything that can be said to an empty room. */
  create: Intent[];
  /** Everything that needed the server to name a lane first, given the
   * `trackCreated` ops the first envelope came back with. */
  fields: (created: ReadonlyMap<string, CreatedLane>) => Intent[];
  /** How many lanes and clips this upload expects to land — what a caller
   * checks the room against before calling a save a success. */
  expect: { tracks: number; clips: number };
}

export function roomUpload(room: CanonicalRoom): RoomUpload {
  const create: Intent[] = [];
  if (room.tempo > 0) create.push({ kind: "setTempo", value: room.tempo, baseRev: 0 });

  // `ord` is display order; the lanes go up in that order so a room with
  // stale or duplicate ords comes back in the order it was seen on screen.
  const tempIdOf = new Map<string, string>();
  const ordered = orderedTracks(room);
  ordered.forEach((track, ord) => {
    const created = createTrack(track.name, ord, track.trackKind ?? "audio", track.instrument);
    tempIdOf.set(track.id, created.tempId);
    create.push(created);
  });

  let clips = 0;
  for (const clip of room.clips) {
    const trackId = tempIdOf.get(clip.trackId);
    if (trackId === undefined) continue; // a clip on no lane cannot be placed
    clips++;
    create.push({
      kind: "upsertClip",
      id: null,
      tempId: mintTempId("upload-clip"),
      trackId,
      baseRev: null,
      clip: clipDataOf(clip),
      audioKey: clip.audioKey,
    });
  }

  if (room.sceneNames && room.sceneNames.length > 0) {
    create.push({ kind: "setSceneNames", names: room.sceneNames, baseRev: 0 });
  }
  if (room.cuePoints && room.cuePoints.length > 0) {
    create.push({ kind: "setCuePoints", points: room.cuePoints, baseRev: 0 });
  }
  if (room.master) {
    create.push({ kind: "setMaster", volume: room.master.volume, pan: room.master.pan, baseRev: 0 });
  }
  if (room.returns && room.returns.length > 0) {
    create.push({ kind: "setReturns", returns: room.returns, baseRev: 0 });
  }
  if (room.scale) {
    create.push({ kind: "setScale", scale: room.scale, baseRev: 0 });
  }

  const fields = (createdLanes: ReadonlyMap<string, CreatedLane>): Intent[] => {
    const out: Intent[] = [];
    for (const track of ordered) {
      const tempId = tempIdOf.get(track.id);
      const lane = tempId === undefined ? undefined : createdLanes.get(tempId);
      if (!lane) continue; // the lane never landed; its fields have nothing to sit on
      const push = (field: TrackField, value: TrackFieldValue): void => {
        out.push({ kind: "setTrackField", id: lane.id, baseRev: lane.rev, field, value });
      };
      // Only what differs from a fresh lane is worth an op.
      if (track.mute) push("mute", true);
      if (track.solo) push("solo", true);
      if (track.arm) push("arm", true);
      if (track.volume !== undefined) push("volume", track.volume);
      if (track.pan !== undefined) push("pan", track.pan);
      if (track.sends && track.sends.length > 0) {
        out.push({ kind: "setTrackSends", id: lane.id, baseRev: lane.rev, sends: track.sends });
      }
      if (track.devices && track.devices.length > 0) {
        // baseRev 0, NOT the source lane's `devicesRev`: the target lane was
        // born in the envelope before this one, so its chain has never been
        // written and its rev is 0. Sending the browser's rev (a hydrated room
        // carries 1) made the merge call every device chain stale and the whole
        // save throw — "the server refused N changes (stale)", one per lane
        // with devices, on a room nobody else had touched.
        out.push({ kind: "setTrackDevices", id: lane.id, baseRev: 0, devices: track.devices });
      }
    }
    return out;
  };

  return { create, fields, expect: { tracks: ordered.length, clips } };
}

/** The `trackCreated` ops of an upload's first envelope, as the map its
 * second envelope wants. */
export function createdLanesOf(accepted: readonly AcceptedOp[]): Map<string, CreatedLane> {
  const out = new Map<string, CreatedLane>();
  for (const op of accepted) {
    if (op.kind === "trackCreated" && op.tempId) out.set(op.tempId, { id: op.id, rev: op.rev });
  }
  return out;
}

/** The lane and, by the room's cascade, every clip on it. */
export function deleteTrack(room: CanonicalRoom, trackId: string): Intent | null {
  const track = room.tracks.find((t) => t.id === trackId);
  return track ? { kind: "deleteTrack", id: track.id, baseRev: track.rev } : null;
}

/** The next display ord — one past the highest lane. */
export function nextOrd(room: CanonicalRoom): number {
  return room.tracks.reduce((m, t) => Math.max(m, t.ord), -1) + 1;
}

/**
 * An empty MIDI clip — what double-clicking a MIDI lane's empty space makes.
 * The wire requires a notes ARRAY on a create, and an empty one is a legal
 * clip (a Live receiver builds `createMidiClip(length)` and writes nothing
 * into it). Geometry is the flattened form every MIDI clip travels in:
 * markers 0..duration, not looping (shared/midi/notes.ts flattenMidiClip).
 */
export function createMidiClip(
  room: CanonicalRoom,
  trackId: string,
  atBeat: number,
  duration: number,
  place: (start: number, duration: number) => number | null,
  content: { notes?: readonly WireNote[]; name?: string } = {},
): Intent | null {
  const track = room.tracks.find((t) => t.id === trackId);
  if (!track || track.trackKind !== "midi") return null;
  const start = place(atBeat, duration);
  if (start === null) return null;
  return midiClipAt(trackId, start, duration, content);
}

/** The create for a MIDI clip at exactly `start` on `trackId` — a lane's id
 * or a same-envelope tempId. Null when the notes are over the clip cap. */
function midiClipAt(
  trackId: string,
  start: number,
  duration: number,
  content: { notes?: readonly WireNote[]; name?: string },
): Extract<Intent, { kind: "upsertClip" }> | null {
  const notes = canonicalNotes(content.notes ?? []);
  if (notes.length > MAX_NOTES_PER_CLIP) return null;
  return {
    kind: "upsertClip",
    id: null,
    tempId: mintTempId("midi"),
    trackId,
    baseRev: null,
    clip: {
      filePath: "",
      startTime: start,
      duration,
      isWarped: false,
      looping: false,
      startMarker: 0,
      endMarker: duration,
      loopStart: 0,
      loopEnd: duration,
      name: content.name ?? "",
      color: MIDI_CLIP_COLOR,
      muted: false,
      warpModeValue: 0,
      clipKind: "midi",
      notes,
      notesDigest: notesDigest(notes),
    },
    audioKey: null,
  };
}

/**
 * A MIDI file's tracks into the arrangement: the first onto `laneId`, every
 * further one onto a NEW MIDI lane named after it (one gesture per lane, so
 * a refused clip never leaves an empty stray lane). Each track's clip lands
 * at `atBeat` plus its own bar offset, so a multi-track file keeps its
 * parts aligned. Clips that find no room on the drop lane are skipped.
 */
export function importMidiTracks(
  room: CanonicalRoom,
  laneId: string,
  atBeat: number,
  tracks: ReadonlyArray<{ name: string; offsetBeats: number; duration: number; notes: WireNote[] }>,
  place: (laneId: string, start: number, duration: number) => number | null,
): Intent[] {
  const intents: Intent[] = [];
  let ord = nextOrd(room);
  tracks.forEach((track, i) => {
    const at = Math.max(0, atBeat + track.offsetBeats);
    if (i === 0) {
      const intent = createMidiClip(room, laneId, at, track.duration, (s, d) => place(laneId, s, d), track);
      if (intent) intents.push(intent);
      return;
    }
    const gestureId = mintTempId("gesture");
    const lane = createTrack(track.name, ord++, "midi");
    // The lane is new, so nothing collides; the clip lands exactly at `at`.
    const clip: Intent | null = createMidiClipOn(lane.tempId, at, track.duration, track);
    if (!clip) return;
    intents.push({ ...lane, gestureId }, { ...clip, gestureId });
  });
  return intents;
}

/** A MIDI create naming a lane the same envelope is creating (its tempId). */
function createMidiClipOn(
  trackTempId: string,
  start: number,
  duration: number,
  content: { notes?: readonly WireNote[]; name?: string },
): Intent | null {
  const notes = canonicalNotes(content.notes ?? []);
  if (notes.length > MAX_NOTES_PER_CLIP) return null;
  return {
    kind: "upsertClip",
    id: null,
    tempId: mintTempId("midi"),
    trackId: trackTempId,
    baseRev: null,
    clip: {
      filePath: "",
      startTime: start,
      duration,
      isWarped: false,
      looping: false,
      startMarker: 0,
      endMarker: duration,
      loopStart: 0,
      loopEnd: duration,
      name: content.name ?? "",
      color: MIDI_CLIP_COLOR,
      muted: false,
      warpModeValue: 0,
      clipKind: "midi",
      notes,
      notesDigest: notesDigest(notes),
    },
    audioKey: null,
  };
}

/** Live's default-ish clip colour for a clip the browser drew. */
const MIDI_CLIP_COLOR = 0x9bc4ff;

/**
 * Split a MIDI clip at `atBeat` into two: the original shortened to the left
 * part, and a NEW clip for the right part carrying the notes that fall after
 * the cut, shifted to be clip-relative again. One gesture, so the shorten
 * never lands without its other half. MIDI only, on purpose: an audio clip's
 * object is its region render, and the wire has no way to say "play this
 * object from its middle" — the right half would sound like the left. Null
 * when the cut misses the clip's interior or would leave a sliver.
 */
export function splitClip(
  room: CanonicalRoom,
  clipId: string,
  atBeat: number,
  minDuration: number,
): Intent[] {
  const clip = findClip(room, clipId);
  if (!clip || clip.slot !== undefined || clip.clipKind !== "midi") return [];
  const offset = atBeat - clip.startTime;
  if (offset < minDuration || clip.duration - offset < minDuration) return [];
  const gestureId = mintTempId("gesture");
  const rightLength = clip.duration - offset;

  const left: Intent = {
    ...upsert(clip, {
      duration: offset,
      endMarker: offset,
      loopEnd: offset,
      ...notesPatch((clip.notes ?? []).filter((n) => n.startTime < offset)),
    }),
    gestureId,
  };
  const right: Intent = {
    kind: "upsertClip",
    id: null,
    tempId: mintTempId("split"),
    trackId: clip.trackId,
    baseRev: null,
    clip: {
      ...clipDataOf(clip),
      startTime: clip.startTime + offset,
      duration: rightLength,
      startMarker: 0,
      endMarker: rightLength,
      loopStart: 0,
      loopEnd: rightLength,
      ...notesPatch(
        (clip.notes ?? [])
          .filter((n) => n.startTime >= offset)
          .map((n) => ({ ...n, startTime: n.startTime - offset })),
      ),
    },
    audioKey: null,
    gestureId,
  };
  return [left, right];
}

function notesPatch(notes: WireNote[]): Pick<ClipData, "notes" | "notesDigest"> {
  const canonical = canonicalNotes(notes);
  return { notes: canonical, notesDigest: notesDigest(canonical) };
}

/**
 * Place one library clip on `trackId` — the paste idiom applied to a
 * LibraryPlacement: a create reusing the newest copy's own key (no upload,
 * passes the room's audioExists gate) with its full geometry carried whole,
 * only startTime rewritten by the caller's collision resolver and `muted`
 * cleared — placing a copy MEANS hearing it, whatever state the source
 * arrangement left it in. Returns null when `place` finds no slot.
 */
export function placeLibraryClip(
  placement: LibraryPlacement,
  trackId: string,
  atBeat: number,
  place: (start: number, duration: number) => number | null,
): Intent | null {
  const start = place(atBeat, placement.clip.duration);
  if (start === null) return null;
  return {
    kind: "upsertClip",
    id: null,
    tempId: mintTempId("lib"),
    trackId,
    baseRev: null,
    clip: { ...placement.clip, startTime: start, muted: false },
    audioKey: placement.audioKey,
  };
}

/**
 * A library clip into an empty Session View slot — the grid's twin of the
 * timeline drop. Same lane kind as the entry (an audio entry on an audio
 * lane, a MIDI one on a MIDI lane), slot free and within the cap; the
 * entry's geometry rides through with `startTime` 0, as every slot clip's.
 */
export function placeLibraryClipInSlot(
  room: CanonicalRoom,
  placement: LibraryPlacement,
  trackId: string,
  slot: number,
): Intent | null {
  const track = room.tracks.find((t) => t.id === trackId);
  if (!track || !Number.isInteger(slot) || slot < 0 || slot >= MAX_SCENES) return null;
  if ((track.trackKind ?? "audio") !== (placement.clip.clipKind ?? "audio")) return null;
  if (room.clips.some((c) => c.trackId === trackId && c.slot === slot)) return null;
  return {
    kind: "upsertClip",
    id: null,
    tempId: mintTempId("lib"),
    trackId,
    baseRev: null,
    clip: { ...placement.clip, startTime: 0, muted: false, slot },
    audioKey: placement.audioKey,
  };
}

/**
 * Place a library clip on a lane that doesn't exist yet: createTrack +
 * upsertClip naming the track's tempId (same-envelope mints resolve — merge
 * engine v4), stamped with one gestureId so the pair lands or dies together
 * and a refused clip never leaves an empty stray lane behind.
 */
export function placeOnNewLane(
  placement: LibraryPlacement,
  name: string,
  ord: number,
  atBeat: number,
): Intent[] {
  const gestureId = mintTempId("gesture");
  const track = createTrack(name, ord);
  // The lane is new, so nothing collides: the clip lands exactly at atBeat.
  const clip = placeLibraryClip(placement, track.tempId, atBeat, (start) => start);
  if (!clip) return [];
  return [
    { ...track, gestureId },
    { ...clip, gestureId },
  ];
}

/**
 * A new instrument track, GarageBand's way: the MIDI lane and an empty clip
 * on it at `atBeat`, ready to draw into. One gesture — the clip names the
 * lane's tempId, so the two land together or not at all. The clip is the
 * one `tempId` of the pair that is a clip's; open it once it lands.
 */
export function newInstrumentLane(
  name: string,
  ord: number,
  instrument: InstrumentId,
  atBeat: number,
  beats: number,
  /** Notes to start with — a library MIDI idea placed on a lane of its own. */
  notes: readonly WireNote[] = [],
): { intents: Intent[]; clipTempId: string } | null {
  const gestureId = mintTempId("gesture");
  const track = createTrack(name, ord, "midi", instrument);
  const clip = midiClipAt(track.tempId, Math.max(0, atBeat), beats, { name, notes });
  if (!clip) return null;
  return {
    intents: [
      { ...track, gestureId },
      { ...clip, gestureId },
    ],
    clipTempId: clip.tempId,
  };
}

export function setTrackField(
  room: CanonicalRoom,
  trackId: string,
  field: TrackField,
  value: TrackFieldValue,
): Intent | null {
  const track = room.tracks.find((t) => t.id === trackId);
  if (!track) return null;
  // A rename from the lane header is the display half only; the ⟨sb:…⟩ token
  // is the track's identity in Live and must survive it.
  const next = field === "name" && typeof value === "string" ? renameKeepingToken(track.name, value) : value;
  return { kind: "setTrackField", id: track.id, baseRev: track.rev, field, value: next };
}

/** Lanes in display order — the order the timeline draws (a stable sort
 * by ord, so equal ords keep the room's insertion order, exactly as
 * arrangement.ts `fromRoom` orders them). One helper, so every reorder is
 * computed against the order the user is looking at. */
export function orderedTracks(room: CanonicalRoom): CanonicalTrackState[] {
  return [...room.tracks].sort((a, b) => a.ord - b.ord);
}

/**
 * Whether lanes may be reordered at all. A receiver that predates lane
 * tokens binds same-named lanes by their ORD-SORTED occurrence, so a room
 * holding two tokenless lanes with one display name would have their
 * bindings swap on the next join — and a repair would then rewrite each
 * lane with the other's clips. Refuse rather than risk it.
 */
export function canReorderTracks(room: CanonicalRoom): boolean {
  const seen = new Set<string>();
  for (const t of room.tracks) {
    const parsed = parseTrackName(t.name);
    if (parsed.token) continue;
    const key = parsed.display.trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
  }
  return true;
}

/**
 * Move a lane to a new position in display order: the lanes are renumbered
 * 0..n−1 in the new order and every lane whose `ord` changed gets one
 * `setTrackOrder` (so a room whose ords had gaps or duplicates comes out
 * clean, and an unmoved lane costs nothing). The set is ONE GESTURE: a
 * stale member (someone revved a lane meanwhile) takes the whole
 * renumbering down rather than landing half of it. `toIndex` is clamped.
 * Empty when the lane isn't there, ends up where it was, or the room can't
 * be reordered safely (`canReorderTracks`).
 */
export function moveTrack(room: CanonicalRoom, trackId: string, toIndex: number): Intent[] {
  if (!canReorderTracks(room)) return [];
  const ordered = orderedTracks(room);
  const from = ordered.findIndex((t) => t.id === trackId);
  if (from < 0) return [];
  const to = Math.max(0, Math.min(ordered.length - 1, Math.round(toIndex)));
  if (to === from) return [];
  const [moved] = ordered.splice(from, 1);
  ordered.splice(to, 0, moved!);
  const intents: Intent[] = ordered.flatMap((t, i) =>
    t.ord === i ? [] : [{ kind: "setTrackOrder" as const, id: t.id, baseRev: t.rev, ord: i }],
  );
  if (intents.length <= 1) return intents;
  const gestureId = mintTempId("gesture");
  return intents.map((i) => ({ ...i, gestureId }));
}

/** One step up or down in display order. */
export function moveTrackBy(room: CanonicalRoom, trackId: string, direction: -1 | 1): Intent[] {
  const from = orderedTracks(room).findIndex((t) => t.id === trackId);
  return from < 0 ? [] : moveTrack(room, trackId, from + direction);
}

/**
 * Replace the Session View's scene names. Names are trimmed of trailing
 * empties only where that changes nothing visible: the list's length also
 * decides how many rows the grid draws, so a trailing "" is a row someone
 * asked for and is kept. Null when nothing would change.
 */
export function setSceneNames(room: CanonicalRoom, names: readonly SceneName[]): Intent | null {
  const current = room.sceneNames ?? [];
  // null = unknown (never written to a Set); "" = cleared (written).
  const next = names.slice(0, MAX_SCENES).map((n) => (n === null ? null : n.slice(0, MAX_SCENE_NAME_LENGTH)));
  if (next.length === current.length && next.every((n, i) => n === current[i])) return null;
  return { kind: "setSceneNames", baseRev: room.scenesRev ?? 0, names: next };
}

/** Name one scene row, padding the list with unnamed rows to reach it. */
export function renameScene(room: CanonicalRoom, row: number, name: string): Intent | null {
  if (!Number.isInteger(row) || row < 0 || row >= MAX_SCENES) return null;
  const names: SceneName[] = [...(room.sceneNames ?? [])];
  while (names.length <= row) names.push(null);
  names[row] = name;
  return setSceneNames(room, names);
}

/** Add an empty scene row after the last one the grid draws (`sceneCount`
 * counts slot-occupied rows too, so the new row lands below all of them). */
export function addScene(room: CanonicalRoom, sceneCount: number): Intent | null {
  const names: SceneName[] = [...(room.sceneNames ?? [])];
  while (names.length < sceneCount) names.push(null);
  names.push(null);
  return setSceneNames(room, names);
}

/** The slot clips in one scene row, and every one below it. */
function slotClipsFrom(room: CanonicalRoom, row: number): { inRow: CanonicalClipState[]; below: CanonicalClipState[] } {
  const inRow: CanonicalClipState[] = [];
  const below: CanonicalClipState[] = [];
  for (const c of room.clips) {
    if (c.slot === undefined) continue;
    if (c.slot === row) inRow.push(c);
    else if (c.slot > row) below.push(c);
  }
  return { inRow, below };
}

/** Shift slot clips by one row. Vacate-before-occupy: moving DOWN starts
 * from the lowest row so each lands in a slot the previous move emptied;
 * moving UP starts from the top. Members fold one by one in a gesture's
 * trial, so this order is what the room sees. */
function shiftSlotClips(clips: CanonicalClipState[], by: 1 | -1): Intent[] {
  const ordered = [...clips].sort((a, b) => (by > 0 ? b.slot! - a.slot! : a.slot! - b.slot!));
  return ordered.map((c) => upsert(c, { startTime: 0, slot: c.slot! + by }));
}

/**
 * An empty scene row at `row`, pushing that row and everything below it
 * down one — Live's Insert Scene. Rows are room state (slots + the names
 * list), so it is one gesture: the moves and the list land or die together.
 * `sceneCount` is what the grid draws, so the names list is padded to cover
 * every occupied row before the blank is inserted.
 */
export function insertScene(room: CanonicalRoom, row: number, sceneCount: number): Intent[] {
  if (!Number.isInteger(row) || row < 0 || row >= MAX_SCENES - 1) return [];
  const { inRow, below } = slotClipsFrom(room, row);
  const moves = shiftSlotClips([...inRow, ...below], 1);
  const names: SceneName[] = [...(room.sceneNames ?? [])];
  while (names.length < Math.max(sceneCount, row)) names.push(null);
  // Asserted-cleared, not unknown: the receiver WRITES "" to the shifted
  // row, so Live's rows follow (a null would leave the old name in place
  // and the scene poll would learn it straight back).
  names.splice(row, 0, "");
  return stampGesture([...moves, { kind: "setSceneNames", baseRev: room.scenesRev ?? 0, names: names.slice(0, MAX_SCENES) }]);
}

/**
 * Live's Duplicate Scene: a copy of the row right under it, its clips copied
 * (audio copies reuse their objects, MIDI copies carry their notes; a
 * key-less audio clip has nothing to copy and is left out), the rows below
 * pushed down, the name repeated. One gesture.
 */
export function duplicateScene(room: CanonicalRoom, row: number, sceneCount: number): Intent[] {
  if (!Number.isInteger(row) || row < 0 || row >= MAX_SCENES - 1) return [];
  const { inRow, below } = slotClipsFrom(room, row);
  const moves = shiftSlotClips(below, 1);
  const copies: Intent[] = inRow
    .filter((c) => c.clipKind === "midi" || c.audioKey !== null)
    .map((c, i) => ({
      kind: "upsertClip",
      id: null,
      tempId: mintTempId(`scene_${i}`),
      trackId: c.trackId,
      baseRev: null,
      clip: { ...clipDataOf(c), startTime: 0, slot: row + 1 },
      audioKey: c.audioKey,
    }));
  const names: SceneName[] = [...(room.sceneNames ?? [])];
  while (names.length < Math.max(sceneCount, row + 1)) names.push(null);
  names.splice(row + 1, 0, names[row] ?? null);
  return stampGesture([...moves, ...copies, { kind: "setSceneNames", baseRev: room.scenesRev ?? 0, names: names.slice(0, MAX_SCENES) }]);
}

/**
 * Delete a scene row: its clips go, everything below moves up, the name
 * leaves the list. One gesture. A receiver's Set keeps its own scene count
 * (the wire only ever grows scenes), so a Set may be left with an empty
 * last row — the musician removes it; nothing of theirs is destroyed.
 */
export function deleteScene(room: CanonicalRoom, row: number, sceneCount: number): Intent[] {
  if (!Number.isInteger(row) || row < 0) return [];
  const { inRow, below } = slotClipsFrom(room, row);
  const names: SceneName[] = [...(room.sceneNames ?? [])];
  while (names.length < sceneCount) names.push(null);
  if (row >= names.length && inRow.length === 0 && below.length === 0) return [];
  names.splice(row, 1);
  // A Set's scene count never shrinks (the SDK creates but never removes
  // rows here), so the list keeps its length and the last row is cleared:
  // written as "", the name that shifted out of it doesn't linger in Live.
  names.push("");
  const deletes = deleteClips(room, inRow.map((c) => c.id));
  const moves = shiftSlotClips(below, -1);
  const list: Intent = { kind: "setSceneNames", baseRev: room.scenesRev ?? 0, names };
  return stampGesture([...deletes, ...moves, list]);
}

function stampGesture(intents: Intent[]): Intent[] {
  if (intents.length <= 1) return intents;
  const gestureId = mintTempId("gesture");
  return intents.map((i) => ({ ...i, gestureId }));
}

/** A locator on the wire. */
export interface Locator {
  name: string;
  time: number;
}

/** Replace the room's locators: sorted by time, names clamped, duplicates
 * (within 1e-4 beats) folded to the last named. Null for an identical list. */
export function setCuePoints(room: CanonicalRoom, points: readonly Locator[]): Intent | null {
  const current = room.cuePoints ?? [];
  const byTime = new Map<number, Locator>();
  for (const p of points) {
    if (!Number.isFinite(p.time) || p.time < 0) continue;
    const time = Math.round(p.time * 1e4) / 1e4;
    byTime.set(time, { name: p.name.slice(0, MAX_CUE_NAME_LENGTH), time });
  }
  const next = [...byTime.values()].sort((a, b) => a.time - b.time).slice(0, MAX_CUE_POINTS);
  const same =
    next.length === current.length &&
    next.every((p, i) => current[i] !== undefined && p.name === current[i]!.name && Math.abs(p.time - current[i]!.time) < 1e-9);
  if (same) return null;
  return { kind: "setCuePoints", baseRev: room.cuePointsRev ?? 0, points: next };
}

export function addCuePoint(room: CanonicalRoom, time: number, name: string): Intent | null {
  const current = room.cuePoints ?? [];
  if (current.some((p) => Math.abs(p.time - time) < 1e-4)) return null;
  return setCuePoints(room, [...current, { name, time }]);
}

export function removeCuePoint(room: CanonicalRoom, time: number): Intent | null {
  const current = room.cuePoints ?? [];
  const next = current.filter((p) => Math.abs(p.time - time) >= 1e-4);
  return next.length === current.length ? null : setCuePoints(room, next);
}

export function renameCuePoint(room: CanonicalRoom, time: number, name: string): Intent | null {
  const current = room.cuePoints ?? [];
  if (!current.some((p) => Math.abs(p.time - time) < 1e-4)) return null;
  return setCuePoints(
    room,
    current.map((p) => (Math.abs(p.time - time) < 1e-4 ? { ...p, name } : p)),
  );
}

/** A lane's send level to return `index` — the whole list travels (padded
 * with silence up to the index), like the extension's poll sends it. Null
 * for an unknown lane, a bad index, or no change. */
export function setTrackSend(room: CanonicalRoom, trackId: string, index: number, level: number): Intent | null {
  const track = room.tracks.find((t) => t.id === trackId);
  if (!track || !Number.isInteger(index) || index < 0 || index >= MAX_RETURNS) return null;
  const sends = [...(track.sends ?? [])];
  while (sends.length <= index) sends.push(0);
  const next = clampVolume(level);
  if (Math.abs((sends[index] ?? 0) - next) < 1e-6) return null;
  sends[index] = next;
  return { kind: "setTrackSends", id: track.id, baseRev: track.rev, sends };
}

/** One parameter on one device of a lane's chain, by index — Live's knob,
 * turned from the browser. The value lands clamped into the parameter's
 * range (a quantized one to a step). Null for an unknown lane, device or
 * parameter, or no change. */
export function setDeviceParam(room: CanonicalRoom, trackId: string, device: number, param: number, value: number): Intent | null {
  const track = room.tracks.find((t) => t.id === trackId);
  const p = track?.devices?.[device]?.params[param];
  if (!track || !p || !Number.isFinite(value)) return null;
  const next = clampParam(value, p);
  if (Math.abs(p.value - next) < 1e-6) return null;
  return { kind: "setDeviceParam", id: track.id, baseRev: track.devicesRev ?? 0, device, param, value: next };
}

/**
 * Insert Live's built-in device `name` into a lane's chain at `index` — a
 * PLACEHOLDER on the wire (the name, no parameters) that every receiver
 * turns into the real device and the next poll fills in. Null for an
 * unknown lane, an unknown chain (nothing to insert into yet), a bad index
 * or an empty name.
 */
export function insertDevice(room: CanonicalRoom, trackId: string, index: number, name: string): Intent | null {
  const track = room.tracks.find((t) => t.id === trackId);
  const trimmed = name.trim().slice(0, MAX_DEVICE_NAME_LENGTH);
  if (!track || !track.devices || !trimmed || !Number.isInteger(index) || index < 0 || index > track.devices.length) return null;
  if (track.devices.length >= MAX_DEVICES) return null;
  const devices = cloneDevices(track.devices);
  devices.splice(index, 0, { name: trimmed, params: [] });
  return { kind: "setTrackDevices", id: track.id, baseRev: track.devicesRev ?? 0, devices };
}

/** Remove the device at `index` from a lane's chain. A receiver whose Set
 * matched the room's chain deletes it; one that didn't only stops writing
 * to it. Null for an unknown lane or index. */
export function removeDevice(room: CanonicalRoom, trackId: string, index: number): Intent | null {
  const track = room.tracks.find((t) => t.id === trackId);
  if (!track || !track.devices || !Number.isInteger(index) || index < 0 || index >= track.devices.length) return null;
  const devices = cloneDevices(track.devices);
  devices.splice(index, 1);
  return { kind: "setTrackDevices", id: track.id, baseRev: track.devicesRev ?? 0, devices };
}

// ---- Non-lane chains: the master's, a return's (slice 39) --------------------

/** One knob on a non-lane chain, by owner ("master" / "return:<i>"). */
export function setChainParam(room: CanonicalRoom, owner: ChainOwner, device: number, param: number, value: number): Intent | null {
  const chain = room.chains?.[owner];
  const p = chain?.devices[device]?.params[param];
  if (!chain || !p || !Number.isFinite(value)) return null;
  const next = clampParam(value, p);
  if (Math.abs(p.value - next) < 1e-6) return null;
  return { kind: "setChainParam", owner, baseRev: chain.rev, device, param, value: next };
}

/** Insert Live's built-in `name` into a non-lane chain at `index` (a placeholder). */
export function insertChainDevice(room: CanonicalRoom, owner: ChainOwner, index: number, name: string): Intent | null {
  const chain = room.chains?.[owner];
  const trimmed = name.trim().slice(0, MAX_DEVICE_NAME_LENGTH);
  if (!chain || chain.rev <= 0 || !trimmed || !Number.isInteger(index) || index < 0 || index > chain.devices.length) return null;
  if (chain.devices.length >= MAX_DEVICES) return null;
  const devices = cloneDevices(chain.devices);
  devices.splice(index, 0, { name: trimmed, params: [] });
  return { kind: "setChain", owner, baseRev: chain.rev, devices };
}

/** Remove the device at `index` from a non-lane chain. */
export function removeChainDevice(room: CanonicalRoom, owner: ChainOwner, index: number): Intent | null {
  const chain = room.chains?.[owner];
  if (!chain || chain.rev <= 0 || !Number.isInteger(index) || index < 0 || index >= chain.devices.length) return null;
  const devices = cloneDevices(chain.devices);
  devices.splice(index, 1);
  return { kind: "setChain", owner, baseRev: chain.rev, devices };
}

/** One of the Set's return tracks, patched — name, mute, solo, fader, pan.
 * The whole list travels under `returnsRev`. Null for a row the room
 * doesn't hold, or no change. */
export function setReturnField(
  room: CanonicalRoom,
  index: number,
  patch: Partial<{ name: string; mute: boolean; solo: boolean; volume: number; pan: number }>,
): Intent | null {
  const returns = (room.returns ?? []).map((r) => ({ ...r }));
  const current = returns[index];
  if (!current) return null;
  const next = {
    ...current,
    ...patch,
    ...(patch.volume !== undefined ? { volume: clampVolume(patch.volume) } : {}),
    ...(patch.pan !== undefined ? { pan: clampPan(patch.pan) } : {}),
  };
  const same =
    next.name === current.name &&
    next.mute === current.mute &&
    next.solo === current.solo &&
    Math.abs(next.volume - current.volume) < 1e-6 &&
    Math.abs(next.pan - current.pan) < 1e-6;
  if (same) return null;
  returns[index] = next;
  return { kind: "setReturns", baseRev: room.returnsRev ?? 0, returns };
}

/** The Set's master fader + pan — one intent for both, like a lane's mixer
 * fields travel one at a time. Null when nothing would change. */
export function setMaster(room: CanonicalRoom, volume: number, pan: number): Intent | null {
  const current = room.master ?? { volume: UNITY_VOLUME, pan: CENTER_PAN };
  const next = { volume: clampVolume(volume), pan: clampPan(pan) };
  if (Math.abs(next.volume - current.volume) < 1e-6 && Math.abs(next.pan - current.pan) < 1e-6) return null;
  return { kind: "setMaster", baseRev: room.masterRev ?? 0, ...next };
}

export function setTempo(room: CanonicalRoom, value: number): Intent {
  return { kind: "setTempo", baseRev: room.tempoRev, value };
}

/** The entity an intent touches. Creates are keyed by tempId — they touch nothing yet. */
export function entityKey(intent: Intent): string {
  switch (intent.kind) {
    case "createTrack":
      return `new:${intent.tempId}`;
    case "setTrackField":
    case "setTrackOrder":
    case "deleteTrack":
      return `track:${intent.id}`;
    case "upsertClip":
      return intent.id === null ? `new:${intent.tempId}` : `clip:${intent.id}`;
    case "deleteClip":
      return `clip:${intent.id}`;
    case "setTempo":
      return "tempo";
    case "setSceneNames":
      return "scenes";
    case "setCuePoints":
      return "cuePoints";
    case "setMaster":
      return "master";
    case "setTrackSends":
      return `track:${intent.id}:sends`;
    case "setTrackInstrument":
      return `track:${intent.id}:instrument`;
    case "setTrackDevices":
      return `track:${intent.id}:devices`;
    case "setDeviceParam":
      // One knob is its own entity: two knobs on one lane in one envelope
      // both land (the merge engine reads the envelope's start rev as fresh).
      return `track:${intent.id}:device:${intent.device}:${intent.param}`;
    case "setChain":
      return `chain:${intent.owner}`;
    case "setChainParam":
      return `chain:${intent.owner}:device:${intent.device}:${intent.param}`;
    case "setReturns":
      return "returns";
    case "setScale":
      return "scale";
  }
}

/**
 * Collapse to at most one intent per entity, keeping the latest.
 *
 * Two intents on one entity in one envelope both cite the same baseRev; the
 * merge engine reads the envelope's start rev as fresh (2026-09-08), so both
 * would land — but the first is a wasted op and a wasted broadcast. Position
 * is inherited from the surviving intent so the caller's vacate-before-occupy
 * ordering still holds.
 */
export function coalesceIntents(intents: Intent[]): Intent[] {
  const lastAt = new Map<string, number>();
  intents.forEach((intent, i) => lastAt.set(entityKey(intent), i));
  return intents.filter((intent, i) => lastAt.get(entityKey(intent)) === i);
}

/**
 * A clip from audio the browser itself put into the pool — a file dropped on
 * a lane, or a slice. Unwarped one-shot with markers spanning its length, the
 * frame a region render's markers sit in (extension clip-audio.ts
 * adoptWireAudio): the object IS the clip, so a Live
 * receiver builds `createAudioClip({filePath, startTime, duration,
 * isWarped: false})` and plays it as is. `durationBeats` is the caller's
 * seconds → beats at the room tempo.
 */
export function createAudioClip(
  room: CanonicalRoom,
  trackId: string,
  atBeat: number,
  durationBeats: number,
  audioKey: string,
  name: string,
  place: (start: number, duration: number) => number | null,
  color = AUDIO_CLIP_COLOR,
  /** Warp the clip at its object's own tempo (slice 48): `durationBeats`
   * is then the object's length in ITS beats, the markers span it, and
   * every Set and browser plays it stretched onto the room's grid. Absent
   * ⇒ an unwarped one-shot that plays at the file's speed. */
  warp?: { sourceTempo: number },
): Intent | null {
  const track = room.tracks.find((t) => t.id === trackId);
  if (!track || (track.trackKind ?? "audio") !== "audio" || !(durationBeats > 0)) return null;
  if (warp && !(warp.sourceTempo > 0)) return null;
  const start = place(atBeat, durationBeats);
  if (start === null) return null;
  return {
    kind: "upsertClip",
    id: null,
    tempId: mintTempId("audio"),
    trackId,
    baseRev: null,
    clip: {
      filePath: "",
      startTime: start,
      duration: durationBeats,
      isWarped: warp !== undefined,
      looping: false,
      startMarker: 0,
      endMarker: durationBeats,
      loopStart: 0,
      loopEnd: durationBeats,
      name,
      color,
      muted: false,
      warpModeValue: 0,
      clipKind: "audio",
      ...(warp ? { sourceTempo: warp.sourceTempo } : {}),
    },
    audioKey,
  };
}

/**
 * A clip from the producer's own library ("Mine") as a placement, for
 * placeLibraryClip / placeOnNewLane. The audio is already a pool object; what
 * this adds is where it came from: `libraryClip` names the clip and its
 * owner, and rides along through every copy, trim, split and save
 * (protocol.ts).
 *
 * A loop with a known tempo goes in WARPED at that tempo (Complex, Live's
 * keep-pitch mode), so it plays in time at whatever tempo the room is at:
 * its length is its own beats, which warping lays onto the room's grid.
 * Anything else — a one-shot, a loop nobody named a tempo for — goes in
 * unwarped, at its own speed, as every new clip in the room does.
 */
export function myClipPlacement(
  clip: { id: string; ownerId: string; audioKey: string; name: string; durationSec: number; bpm: number | null },
  tempo: number,
): LibraryPlacement {
  const placement = audioFilePlacement(clip, tempo);
  return { ...placement, clip: { ...placement.clip, libraryClip: { id: clip.id, ownerId: clip.ownerId } } };
}

/**
 * Audio the browser put into the room itself — a file dropped from the
 * desktop — as a placement, by the same rule as a library clip: a known
 * tempo goes in warped at it (Complex), anything else plays at its own speed.
 * No `libraryClip`: it came from nowhere but this room.
 */
export function audioFilePlacement(
  clip: { audioKey: string; name: string; durationSec: number; bpm: number | null },
  tempo: number,
): LibraryPlacement {
  const bpm = clip.bpm !== null && clip.bpm >= 20 && clip.bpm <= 999 ? clip.bpm : null;
  const raw = (clip.durationSec * (bpm ?? tempo)) / 60;
  // A loop cut to whole beats reads a hair off them (its length is stored in
  // whole milliseconds): put it back on the grid so it repeats in time.
  const beats = bpm !== null && Math.abs(raw - Math.round(raw)) < 0.01 ? Math.round(raw) : raw;
  return {
    audioKey: clip.audioKey,
    clip: {
      filePath: "",
      startTime: 0,
      duration: beats,
      isWarped: bpm !== null,
      looping: false,
      startMarker: 0,
      endMarker: beats,
      loopStart: 0,
      loopEnd: beats,
      name: clip.name,
      color: AUDIO_CLIP_COLOR,
      muted: false,
      warpModeValue: bpm !== null ? WARP_MODE_COMPLEX : 0,
      clipKind: "audio",
      ...(bpm !== null ? { sourceTempo: bpm } : {}),
    },
  };
}

/** Live's Complex warp mode: stretch keeping pitch, for any material. */
const WARP_MODE_COMPLEX = 4;

const AUDIO_CLIP_COLOR = 0xffa529;

/**
 * Split an AUDIO clip at `atBeat`, given a pool key the caller has already
 * uploaded holding the right-hand part of the clip's audio (sliced from the
 * decoded object at the cut — the wire can't say "play from the middle", so
 * the bytes have to be their own object). The left half is an end-edge
 * resize under `resizeClip`'s marker discipline and keeps its object (a
 * clip plays the first `duration` beats of it); the right half is a fresh
 * unwarped one-shot on the new key. One gesture, so the shorten never lands
 * without its other half. Empty when the cut misses the interior.
 */
export function splitAudioClip(
  room: CanonicalRoom,
  clipId: string,
  atBeat: number,
  minDuration: number,
  rightAudioKey: string,
): Intent[] {
  const clip = findClip(room, clipId);
  if (!clip || clip.slot !== undefined || clip.clipKind === "midi") return [];
  const offset = atBeat - clip.startTime;
  if (offset < minDuration || clip.duration - offset < minDuration) return [];
  const gestureId = mintTempId("gesture");
  const rightLength = clip.duration - offset;
  const left = resizeClip(room, clipId, "end", -rightLength, minDuration);
  if (!left) return [];
  const right: Intent = {
    kind: "upsertClip",
    id: null,
    tempId: mintTempId("split"),
    trackId: clip.trackId,
    baseRev: null,
    clip: {
      ...clipDataOf(clip),
      filePath: "",
      startTime: clip.startTime + offset,
      duration: rightLength,
      isWarped: false,
      looping: false,
      startMarker: 0,
      endMarker: rightLength,
      loopStart: 0,
      loopEnd: rightLength,
      warpModeValue: 0,
    },
    audioKey: rightAudioKey,
    gestureId,
  };
  return [{ ...left, gestureId }, right];
}

/**
 * Move a clip into a Session View slot (or between slots). The place is the
 * slot; startTime is 0 by convention on a slot clip. The room refuses an
 * occupied slot as `overlap` and a kind mismatch as `invalid`, so the caller
 * checks the lane's kind; occupancy is checked here against the view we hold.
 */
export function moveClipToSlot(
  room: CanonicalRoom,
  clipId: string,
  trackId: string,
  slot: number,
): Intent | null {
  const clip = findClip(room, clipId);
  const track = room.tracks.find((t) => t.id === trackId);
  if (!clip || !track || !Number.isInteger(slot) || slot < 0) return null;
  if ((track.trackKind ?? "audio") !== (clip.clipKind ?? "audio")) return null;
  if (clip.trackId === trackId && clip.slot === slot) return null;
  const occupied = room.clips.some((c) => c.id !== clipId && c.trackId === trackId && c.slot === slot);
  if (occupied) return null;
  return upsert(clip, { startTime: 0, slot }, trackId);
}

/**
 * A COPY of any clip — timeline or Session View — into an empty slot: the
 * pads' Duplicate, Paste, and "keep this loop as a scene clip". The source
 * stays put; the copy reuses its object or carries its notes.
 *
 * `braceWarped` gives a warped, unlooped source the same loop brace a ghost
 * pad plays with (lib/ghost-clips.ts): a warped clip's object is the whole
 * source file, so a slot clip without a brace would loop the rest of the file
 * instead of the bars that were on the pad. Null when the clip is gone, has
 * nothing to copy, the lane is the wrong kind, or the slot is taken.
 */
export function copyClipToSlot(
  room: CanonicalRoom,
  clipId: string,
  trackId: string,
  slot: number,
  options: { braceWarped?: boolean } = {},
): Intent | null {
  const clip = findClip(room, clipId);
  const track = room.tracks.find((t) => t.id === trackId);
  if (!clip || !track || !Number.isInteger(slot) || slot < 0 || slot >= MAX_SCENES) return null;
  if (clip.clipKind !== "midi" && clip.audioKey === null) return null;
  if ((track.trackKind ?? "audio") !== (clip.clipKind ?? "audio")) return null;
  if (room.clips.some((c) => c.trackId === trackId && c.slot === slot)) return null;
  const data: ClipData = { ...clipDataOf(clip), startTime: 0, muted: false, slot };
  const braced = data.looping && data.loopEnd - data.loopStart > 1e-6;
  if (options.braceWarped && data.isWarped && !braced) {
    const start = Math.max(0, data.startMarker);
    data.looping = true;
    data.loopStart = start;
    data.loopEnd = data.endMarker > start ? data.endMarker : start + data.duration;
  }
  return {
    kind: "upsertClip",
    id: null,
    tempId: mintTempId("slot"),
    trackId,
    baseRev: null,
    clip: data,
    audioKey: clip.audioKey,
  };
}

/**
 * Consolidate: replace several timeline audio clips on ONE lane with a single
 * clip spanning them, on an object the caller has already rendered and
 * uploaded (the mix of those clips over [first start, last end] — an
 * OfflineAudioContext's job, not this file's). Deletes and the create travel
 * as one gesture, so the originals never go without their replacement.
 * Empty when the clips aren't all audio, on one lane, on the timeline.
 */
export function consolidateClips(
  room: CanonicalRoom,
  clipIds: string[],
  audioKey: string,
  name: string,
): Intent[] {
  const clips = clipIds.map((id) => findClip(room, id)).filter((c): c is CanonicalClipState => c !== null);
  if (clips.length < 1) return [];
  const laneId = clips[0]!.trackId;
  if (clips.some((c) => c.trackId !== laneId || c.slot !== undefined || c.clipKind === "midi")) return [];
  const span = consolidatedSpan(clips);
  const gestureId = mintTempId("gesture");
  const first = [...clips].sort((a, b) => a.startTime - b.startTime)[0]!;
  const create: Intent = {
    kind: "upsertClip",
    id: null,
    tempId: mintTempId("bounce"),
    trackId: laneId,
    baseRev: null,
    clip: {
      filePath: "",
      startTime: span.start,
      duration: span.end - span.start,
      isWarped: false,
      looping: false,
      startMarker: 0,
      endMarker: span.end - span.start,
      loopStart: 0,
      loopEnd: span.end - span.start,
      name,
      color: first.color,
      muted: false,
      warpModeValue: 0,
      clipKind: "audio",
    },
    audioKey,
    gestureId,
  };
  return [...deleteClips(room, clips.map((c) => c.id)).map((i) => ({ ...i, gestureId })), create];
}

/** The beat span a set of clips covers. */
export function consolidatedSpan(clips: readonly CanonicalClipState[]): { start: number; end: number } {
  return {
    start: Math.min(...clips.map((c) => c.startTime)),
    end: Math.max(...clips.map((c) => c.startTime + c.duration)),
  };
}

/**
 * An empty MIDI clip in a Session View slot — what double-clicking an empty
 * cell on a MIDI lane makes. Same flattened, note-less create as
 * `createMidiClip`, placed by SLOT rather than beat (startTime 0 by the
 * wire's convention); the room refuses an occupied slot as `overlap`, so
 * occupancy is checked here against the view we hold. `duration` is the
 * clip's own length (one bar), which a receiver builds with
 * `ClipSlot.createMidiClip(length)`.
 */
export function createMidiSlotClip(
  room: CanonicalRoom,
  trackId: string,
  slot: number,
  duration: number,
): Intent | null {
  const track = room.tracks.find((t) => t.id === trackId);
  if (!track || track.trackKind !== "midi" || !Number.isInteger(slot) || slot < 0) return null;
  if (room.clips.some((c) => c.trackId === trackId && c.slot === slot)) return null;
  const base = createMidiClip(room, trackId, 0, duration, (s) => s);
  if (!base || base.kind !== "upsertClip") return null;
  return { ...base, tempId: mintTempId("slot"), clip: { ...base.clip, startTime: 0, slot } };
}

/**
 * A recorded take into an empty Session View slot: an unwarped one-shot on
 * an audio lane, its object just uploaded, launched later like any slot
 * clip (it loops over its whole object). Null for a MIDI lane, an occupied
 * or bad slot, or no length.
 */
export function createAudioSlotClip(
  room: CanonicalRoom,
  trackId: string,
  slot: number,
  durationBeats: number,
  audioKey: string,
  name: string,
  color = AUDIO_CLIP_COLOR,
  /** `looped`: a rendered loop of exactly `durationBeats` at the room's
   * tempo — warped (Beats) and looping over its whole object, so it keeps
   * time with the MIDI clips in its scene. A take is an unwarped one-shot. */
  options: { looped?: boolean } = {},
): Intent | null {
  const track = room.tracks.find((t) => t.id === trackId);
  if (!track || (track.trackKind ?? "audio") !== "audio" || !(durationBeats > 0)) return null;
  if (!Number.isInteger(slot) || slot < 0 || slot >= MAX_SCENES) return null;
  if (room.clips.some((c) => c.trackId === trackId && c.slot === slot)) return null;
  const looped = options.looped === true;
  return {
    kind: "upsertClip",
    id: null,
    tempId: mintTempId("slot"),
    trackId,
    baseRev: null,
    clip: {
      filePath: "",
      startTime: 0,
      duration: durationBeats,
      isWarped: looped,
      looping: looped,
      startMarker: 0,
      endMarker: durationBeats,
      loopStart: 0,
      loopEnd: durationBeats,
      name,
      color,
      muted: false,
      warpModeValue: 0,
      slot,
    },
    audioKey,
  };
}

/**
 * Move a Session View clip onto the timeline at `atBeat` — the inverse of
 * `moveClipToSlot`. The slot is dropped from the payload (the fold clears a
 * stale slot only when the op carries none), the start is the placer's, and
 * everything else — object, notes, geometry — rides through untouched. Null
 * when the clip isn't a slot clip, the lane kind mismatches, or the placer
 * finds no room.
 */
export function moveSlotClipToTimeline(
  room: CanonicalRoom,
  clipId: string,
  trackId: string,
  atBeat: number,
  place: (start: number, duration: number) => number | null,
): Intent | null {
  const clip = findClip(room, clipId);
  const track = room.tracks.find((t) => t.id === trackId);
  if (!clip || !track || clip.slot === undefined) return null;
  if ((track.trackKind ?? "audio") !== (clip.clipKind ?? "audio")) return null;
  const start = place(Math.max(0, atBeat), clip.duration);
  if (start === null) return null;
  const { slot: _slot, ...data } = clipDataOf(clip);
  return {
    kind: "upsertClip",
    id: clip.id,
    tempId: `edit_${clip.id}`,
    trackId,
    baseRev: clip.rev,
    clip: { ...data, startTime: start },
    audioKey: null,
  };
}

/**
 * A COPY of any clip — Session View or timeline — onto a lane at `atBeat`.
 *
 * This is what dragging a piece of the room's own material off the shelf and
 * onto the arrangement means: the source stays exactly where it is, the copy
 * reuses its object or carries its notes, and it lands on whichever lane took
 * the drop as long as that lane is the same kind. Null when the clip is gone,
 * has nothing to copy, the lane is the wrong kind, or the placer finds no
 * room.
 */
export function copyClipToLane(
  room: CanonicalRoom,
  clipId: string,
  trackId: string,
  atBeat: number,
  place: (start: number, duration: number) => number | null,
): Intent | null {
  const clip = findClip(room, clipId);
  if (!clip) return null;
  if (clip.clipKind !== "midi" && clip.audioKey === null) return null;
  const track = room.tracks.find((t) => t.id === trackId);
  if (!track || (track.trackKind ?? "audio") !== (clip.clipKind ?? "audio")) return null;
  const start = place(Math.max(0, atBeat), clip.duration);
  if (start === null) return null;
  const { slot: _slot, ...data } = clipDataOf(clip);
  return {
    kind: "upsertClip",
    id: null,
    tempId: mintTempId("capture"),
    trackId,
    baseRev: null,
    clip: { ...data, startTime: start },
    audioKey: clip.audioKey,
  };
}

/**
 * A COPY of a clip on a lane that doesn't exist yet: a new track of the
 * clip's kind (named after the source lane, playing its instrument) with the
 * copy at `atBeat` — material dropped below the last lane. One gesture, so a
 * refused copy never leaves an empty stray lane.
 */
export function copyClipToNewLane(room: CanonicalRoom, clipId: string, ord: number, atBeat: number): Intent[] {
  const clip = findClip(room, clipId);
  if (!clip) return [];
  const midi = clip.clipKind === "midi";
  if (!midi && clip.audioKey === null) return [];
  const source = room.tracks.find((t) => t.id === clip.trackId);
  const gestureId = mintTempId("gesture");
  // The source's name as read — never its lane token (⟨sb:…⟩), which names
  // the source lane and must not be on two.
  const name = (source ? parseTrackName(source.name).display.trim() : "") || (midi ? "MIDI" : "Audio");
  const track = createTrack(name, ord, midi ? "midi" : "audio", source?.instrument);
  const { slot: _slot, ...data } = clipDataOf(clip);
  return [
    { ...track, gestureId },
    {
      kind: "upsertClip",
      id: null,
      tempId: mintTempId("capture"),
      trackId: track.tempId,
      baseRev: null,
      clip: { ...data, startTime: Math.max(0, atBeat) },
      audioKey: clip.audioKey,
      gestureId,
    },
  ];
}

/**
 * MOVE a clip onto a lane that doesn't exist yet: a new track of the clip's
 * kind (named after the source lane, playing its instrument) with the clip
 * `deltaBeats` along — a clip dragged below the last lane, as Live does it.
 * One gesture, so a refused move never leaves an empty stray lane.
 */
export function moveClipToNewLane(room: CanonicalRoom, clipId: string, ord: number, deltaBeats: number): Intent[] {
  const clip = findClip(room, clipId);
  if (!clip) return [];
  const midi = clip.clipKind === "midi";
  const source = room.tracks.find((t) => t.id === clip.trackId);
  const gestureId = mintTempId("gesture");
  // The source's name as read — never its lane token (⟨sb:…⟩).
  const name = (source ? parseTrackName(source.name).display.trim() : "") || (midi ? "MIDI" : "Audio");
  const track = createTrack(name, ord, midi ? "midi" : "audio", source?.instrument);
  return [
    { ...track, gestureId },
    { ...upsert(clip, { startTime: Math.max(0, clip.startTime + deltaBeats) }, track.tempId), gestureId },
  ];
}

/**
 * A COPY of a Session View clip onto its own lane's timeline at `atBeat` —
 * what capturing a launched clip into the arrangement means. `copyClipToLane`
 * with the source's own lane, and refusing anything that is not a slot clip:
 * capture is about what the Session View is playing.
 */
export function copySlotClipToTimeline(
  room: CanonicalRoom,
  clipId: string,
  atBeat: number,
  place: (start: number, duration: number) => number | null,
): Intent | null {
  const clip = findClip(room, clipId);
  if (!clip || clip.slot === undefined) return null;
  return copyClipToLane(room, clipId, clip.trackId, atBeat, place);
}

/**
 * Duplicate a lane: a new track of the same kind right after it in display
 * order, carrying copies of every clip (timeline AND slot) — audio copies
 * reuse their objects, MIDI copies carry their notes. One gesture, so a
 * refused copy never leaves a half-built lane. Mixer, mute and solo can't
 * ride along: they are field ops on a track the room hasn't named yet.
 */
export function duplicateTrack(room: CanonicalRoom, trackId: string): Intent[] {
  const track = room.tracks.find((t) => t.id === trackId);
  if (!track) return [];
  const gestureId = mintTempId("gesture");
  // Right after the source in DISPLAY order: the copy takes the next slot
  // and every lane from there on moves down one, in the same gesture — a
  // fixed `ord + 1` would tie with the next lane and sort after it.
  const ordered = orderedTracks(room);
  const from = ordered.findIndex((t) => t.id === trackId);
  const created = createTrack(`${track.name} copy`, from + 1, track.trackKind ?? "audio", track.instrument);
  const intents: Intent[] = [{ ...created, gestureId }];
  ordered.forEach((t, i) => {
    const want = i <= from ? i : i + 1;
    if (t.ord !== want) intents.push({ kind: "setTrackOrder", id: t.id, baseRev: t.rev, ord: want, gestureId });
  });
  for (const clip of room.clips.filter((c) => c.trackId === trackId)) {
    const midi = clip.clipKind === "midi";
    if (!midi && clip.audioKey === null) continue; // the room could never serve it
    intents.push({
      kind: "upsertClip",
      id: null,
      tempId: mintTempId("dup"),
      trackId: created.tempId,
      baseRev: null,
      clip: clipDataOf(clip),
      audioKey: clip.audioKey,
      gestureId,
    });
  }
  return intents;
}

/**
 * Swap the object behind an audio clip — the wire's only way to say "the
 * samples changed" (a reverse, a normalize, a fade rendered in the browser
 * and uploaded). Geometry rides through untouched, so every Live receiver
 * takes the "same geometry, new audioKey" branch: a rebuild from the new
 * file, never a hole. Null for a MIDI clip or a key-less one.
 */
export function replaceClipAudio(
  room: CanonicalRoom,
  clipId: string,
  audioKey: string,
  patch: Pick<Partial<ClipData>, "name"> = {},
): Intent | null {
  const clip = findClip(room, clipId);
  if (!clip || clip.clipKind === "midi" || clip.audioKey === null) return null;
  const base = upsert(clip, patch);
  return base.kind === "upsertClip" ? { ...base, audioKey } : null;
}

/**
 * Swap a clip's object for a repitched render whose LENGTH changed with the
 * pitch (a semitone up is ~6% shorter — the render was made by playing the
 * object faster). The new object is a fresh flattened one-shot, so the
 * geometry becomes the create-shape (unwarped, markers 0..duration) and the
 * duration follows the render. Refused when the new length would run into
 * the next clip on the lane — the room would refuse it too, and a partial
 * swap is worse than none. Audio only, timeline only.
 */
export function repitchClip(
  room: CanonicalRoom,
  clipId: string,
  audioKey: string,
  newDuration: number,
  name?: string,
): Intent | null {
  const clip = findClip(room, clipId);
  if (!clip || clip.clipKind === "midi" || clip.audioKey === null || clip.slot !== undefined) return null;
  if (!(newDuration > 0)) return null;
  const end = clip.startTime + newDuration;
  const blocked = room.clips.some(
    (other) =>
      other.id !== clip.id &&
      other.trackId === clip.trackId &&
      other.slot === undefined &&
      clip.startTime < other.startTime + other.duration - 1e-4 &&
      other.startTime < end - 1e-4,
  );
  if (blocked) return null;
  const base = upsert(clip, {
    duration: newDuration,
    isWarped: false,
    looping: false,
    startMarker: 0,
    endMarker: newDuration,
    loopStart: 0,
    loopEnd: newDuration,
    ...(name !== undefined ? { name } : {}),
  });
  return base.kind === "upsertClip" ? { ...base, audioKey } : null;
}

/**
 * Move the selected timeline clips one lane up or down (arrow keys). Each
 * clip goes to the nearest lane in that direction of ITS kind (an audio
 * clip skips over MIDI lanes and vice versa) and only when the same span is
 * free there; the rest stay put rather than being proposed into an overlap.
 */
export function moveClipsToLane(room: CanonicalRoom, clipIds: string[], direction: -1 | 1): Intent[] {
  const lanes = [...room.tracks].sort((a, b) => a.ord - b.ord);
  // Pass 1: where each selected clip WOULD go. Only clips with a target lane
  // count as moving; the rest keep their place and still occupy it.
  const moves: Array<{ clip: CanonicalClipState; target: CanonicalTrackState }> = [];
  for (const id of clipIds) {
    const clip = findClip(room, id);
    if (!clip || clip.slot !== undefined) continue;
    const kind = clip.clipKind ?? "audio";
    const from = lanes.findIndex((t) => t.id === clip.trackId);
    if (from < 0) continue;
    for (let i = from + direction; i >= 0 && i < lanes.length; i += direction) {
      if ((lanes[i]!.trackKind ?? "audio") === kind) {
        moves.push({ clip, target: lanes[i]! });
        break;
      }
    }
  }
  const movingIds = new Set(moves.map((m) => m.clip.id));
  const overlap = (a: { startTime: number; duration: number }, b: { startTime: number; duration: number }) =>
    a.startTime < b.startTime + b.duration - 1e-4 && b.startTime < a.startTime + a.duration - 1e-4;
  // Pass 2: against the lane as it will be — clips that stay, plus other
  // movers landing on the same lane.
  const intents: Intent[] = [];
  for (const { clip, target } of moves) {
    const staying = room.clips.some(
      (other) => other.trackId === target.id && other.slot === undefined && !movingIds.has(other.id) && overlap(clip, other),
    );
    const arriving = moves.some((m) => m.clip.id !== clip.id && m.target.id === target.id && overlap(clip, m.clip));
    if (staying || arriving) continue;
    intents.push(upsert(clip, {}, target.id));
  }
  return intents;
}

/**
 * Consolidate several timeline MIDI clips on ONE lane into a single clip
 * spanning them, its notes re-based to the new start. Pure — nothing to
 * render or upload, unlike the audio consolidate. One gesture: the deletes
 * never land without the create. Empty when the clips aren't all MIDI, on
 * one lane, on the timeline, or would exceed the per-clip note cap.
 */
export function consolidateMidiClips(room: CanonicalRoom, clipIds: string[], name?: string): Intent[] {
  const clips = clipIds.map((id) => findClip(room, id)).filter((c): c is CanonicalClipState => c !== null);
  if (clips.length < 2) return [];
  const laneId = clips[0]!.trackId;
  if (clips.some((c) => c.trackId !== laneId || c.slot !== undefined || c.clipKind !== "midi")) return [];
  const span = consolidatedSpan(clips);
  const length = span.end - span.start;
  const merged: WireNote[] = [];
  for (const c of clips) {
    const offset = c.startTime - span.start;
    for (const n of c.notes ?? []) merged.push({ ...n, startTime: n.startTime + offset });
  }
  const canonical = canonicalNotes(merged);
  if (canonical.length > MAX_NOTES_PER_CLIP) return [];
  const first = [...clips].sort((a, b) => a.startTime - b.startTime)[0]!;
  const gestureId = mintTempId("gesture");
  const create: Intent = {
    kind: "upsertClip",
    id: null,
    tempId: mintTempId("merge"),
    trackId: laneId,
    baseRev: null,
    clip: {
      ...clipDataOf(first),
      startTime: span.start,
      duration: length,
      startMarker: 0,
      endMarker: length,
      loopStart: 0,
      loopEnd: length,
      looping: false,
      name: name ?? first.name,
      notes: canonical,
      notesDigest: notesDigest(canonical),
    },
    audioKey: null,
    gestureId,
  };
  return [...deleteClips(room, clips.map((c) => c.id)).map((d) => ({ ...d, gestureId })), create];
}
